// src/components/Dashboard/VentaRunningTotal.js
import React, { useEffect, useState } from "react";
import { Card, Paper, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

import { useTranslation } from "react-i18next";
import { numberLocaleFromI18n } from "../../utils/i18nLocale";
import DashboardChartTooltip from "./DashboardChartTooltip";
import { dashboardFixedTooltipProps } from "../../utils/dashboardChartUtils";
import { getValidVentasRows } from "../../utils/ventasIreneUtils";
import dayjs from "dayjs";
import { pageChartCardSx, pageChartTitleSx } from "../../styles/pageModernStyles";

// 🌍 Locales para dayjs
import "dayjs/locale/es";
import "dayjs/locale/en";
import "dayjs/locale/zh-cn";

const VentaRunningTotal = ({ resumenRows = [], year }) => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [reps, setReps] = useState([]);

  const { i18n, t } = useTranslation();

  // 🌐 Sincroniza el idioma de i18n con dayjs
  useEffect(() => {
    let lang = i18n.language.toLowerCase();
    if (lang === "ch" || lang.startsWith("zh")) lang = "zh-cn";
    else if (lang.startsWith("en")) lang = "en";
    else if (lang.startsWith("es")) lang = "es";
    else lang = "en"; // fallback

    dayjs.locale(lang);
  }, [i18n.language]);

  // 📊 Carga de ventas acumuladas
  useEffect(() => {
    const ventas = getValidVentasRows(resumenRows).filter(
      (row) => Number(row.YEAR) === year,
    );

    const grouped = {};
    ventas.forEach((row) => {
      const month = row.MONTH;
      const rep = row.REPALIAS;

      if (!grouped[month]) grouped[month] = { month };
      grouped[month][rep] =
        (grouped[month][rep] || 0) + (Number(row.Sales) || 0);
    });

    const months = Array.from({ length: 12 }, (_, i) => i + 1);
    const repsUnicos = [...new Set(ventas.map((r) => r.REPALIAS))];

    const acumulados = {};
    const dataFinal = months.map((m) => {
      const row = {
        month: dayjs()
          .month(m - 1)
          .format("MMM"),
      };

      repsUnicos.forEach((rep) => {
        acumulados[rep] = (acumulados[rep] || 0) + (grouped[m]?.[rep] || 0);
        row[rep] = acumulados[rep];
      });

      return row;
    });

    setData(dataFinal);
    setReps(repsUnicos);
  }, [resumenRows, year, i18n.language]);

  const colors = [
    "#00c2a3",
    "#90b2b7",
    "#f28b82",
    "#4285f4",
    "#fbbc04",
    "#34a853",
    "#a142f4",
  ];

  return (
    <Card component={Paper} sx={pageChartCardSx(theme)}>
      <Typography variant="h6" sx={pageChartTitleSx}>
        {t("DashboardVentas.AccumulatedSales")} ({year})
      </Typography>

      <ResponsiveContainer width="100%" height="85%">
        <LineChart data={data}>
          <XAxis
            dataKey="month"
            tick={{ fontSize: 12, fill: theme.palette.text.primary }}
          />
          <YAxis
            width={70}
            tick={{ fontSize: 12, fill: theme.palette.text.primary }}
            tickFormatter={(value) =>
              value >= 1_000_000
                ? `${(value / 1_000_000).toFixed(1)}M`
                : value.toLocaleString(numberLocaleFromI18n(i18n.language))
            }
          />
          <Tooltip
            {...dashboardFixedTooltipProps}
            content={(props) => (
              <DashboardChartTooltip
                {...props}
                valueFormatter={(value) =>
                  value.toLocaleString(numberLocaleFromI18n(i18n.language), {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                }
              />
            )}
          />
          <Legend
            verticalAlign="bottom"
            align="center"
            wrapperStyle={{
              fontSize: "11px",
              marginTop: "-10px",
              paddingBottom: 0,
            }}
            content={(props) => {
              const { payload } = props;
              return (
                <ul
                  style={{
                    fontSize: "11px",
                    listStyle: "none",
                    margin: 0,
                  }}
                >
                  {payload.map((entry, index) => (
                    <li
                      key={`item-${index}`}
                      style={{
                        color: entry.color,
                        marginTop: 0,
                        marginLeft: "4em",
                        marginBottom: "0px",
                      }}
                    >
                      {entry.value}
                    </li>
                  ))}
                </ul>
              );
            }}
          />

          {reps.map((rep, idx) => (
            <Line
              key={rep}
              type="monotone"
              dataKey={rep}
              stroke={colors[idx % colors.length]}
              strokeWidth={2}
              dot={{ r: 3 }}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
};

export default VentaRunningTotal;
